import React, { useEffect, useMemo, useState } from "react";
import AssignedPlansList from "../components/AssignedPlansList";
import PlanDetailCard from "../components/PlanDetailCard";
import { api } from "../utils/api";
import type { FoodPlan, WorkoutPlan } from "../types/api";
import "../styles/plans.css";

type PlanTab = "workout" | "food";
type PlanSummary = {
  id: string;
  name: string;
  type: PlanTab;
};

const PlansPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<PlanTab>("workout");
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [foodPlans, setFoodPlans] = useState<FoodPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<{
    planId: string;
    type: PlanTab;
  } | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const [wo, fd] = await Promise.all([
          api.get<WorkoutPlan[]>("/api/user/plans/workout"),
          api.get<FoodPlan[]>("/api/user/plans/food"),
        ]);
        setWorkoutPlans(wo || []);
        setFoodPlans(fd || []);
      } catch (e) {
        console.error("Plans fetch error", e);
      }
      setLoading(false);
    };
    fetchPlans();
  }, []);

  const workoutSummary = useMemo<PlanSummary[]>(
    () =>
      workoutPlans.map((p) => ({
        id: p.id,
        name: p.title || "Workout Plan",
        type: "workout" as const,
      })),
    [workoutPlans],
  );

  const foodSummary = useMemo<PlanSummary[]>(
    () =>
      foodPlans.map((p) => ({
        id: p.id,
        name: p.title || "Food Plan",
        type: "food" as const,
      })),
    [foodPlans],
  );

  const currentSummary = activeTab === "workout" ? workoutSummary : foodSummary;

  const onExpand = (plan: PlanSummary) => {
    setExpanded({ planId: plan.id, type: plan.type });
  };

  const onCloseDetail = () => setExpanded(null);

  const isEmpty =
    (activeTab === "workout" ? workoutPlans : foodPlans).length === 0;

  return (
    <div className="plans-page">
      <h1 className="plans-title">Plans</h1>
      <div className="plans-tabs" role="tablist" aria-label="Plans tabs">
        <button
          className={`tab ${activeTab === "workout" ? "active" : ""}`}
          onClick={() => setActiveTab("workout")}
          role="tab"
          aria-selected={activeTab === "workout"}
        >
          Workout Plans
        </button>
        <button
          className={`tab ${activeTab === "food" ? "active" : ""}`}
          onClick={() => setActiveTab("food")}
          role="tab"
          aria-selected={activeTab === "food"}
        >
          Food Plans
        </button>
      </div>

      <div className="plans-grid">
        <section className="plans-list-section">
          {loading ? (
            <div className="empty-state">Loading plans…</div>
          ) : isEmpty ? (
            <div className="empty-state">
              No plans assigned yet. Your trainer will share plans soon!
            </div>
          ) : (
            <AssignedPlansList
              plans={currentSummary}
              onExpand={onExpand}
              expandedId={expanded?.planId}
            />
          )}
        </section>

        <section className="plans-detail-section" aria-label="Plan detail view">
          {expanded ? (
            <PlanDetailCard
              planId={expanded.planId}
              type={expanded.type}
              onClose={onCloseDetail}
            />
          ) : (
            <div className="empty-state">Select a plan to view details</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PlansPage;
